import { useContext, useRef, useCallback, useEffect } from "react";
import { ConfigContext } from "../context/ConfigContext";
import { PriceDispatch } from "../context/PriceContext";
import { TextSectionsListContext } from "../context/TextSectionsListContext";
import priceService from "../services/PriceService";
import { resolveFinishAndColor, resolveMounting } from "../services/erpMapping";

/**
 * Buduje żądanie wyceny CAŁEJ konfiguracji (jedno żądanie zamiast osobnego
 * dla każdej sekcji). Sekcje bez tekstu są pomijane.
 *
 * @param {Object} config - stan ConfigContext
 * @param {Array} sections - sekcje tekstu
 * @param {Object} [textOverride] - { index, rawContent } nadpisujące treść sekcji
 * @returns {Object|null} payload dla /letter-configuration-price lub null, gdy nie ma czego wyceniać
 */
export function buildPricingRequest(config, sections, textOverride) {
    const fontErpId = parseInt(config?.selectedFontObject?.font?.[0]?.id_erp, 10) || 0;
    const globalFontSizeErpId = parseInt(config?.selectedFontSizeObject?.id_erp, 10) || 0;
    const { typeErpId, colorErpId } = resolveFinishAndColor(config);
    // Mocowanie efektywne: tryb produktu + wybór użytkownika + blokada taśmy dla małych rozmiarów.
    const mounting = resolveMounting(config, sections);

    const lines = sections
        .map((section, index) => {
            const rawContent =
                textOverride && textOverride.index === index
                    ? textOverride.rawContent
                    : section.rawContent;

            if (!rawContent || rawContent.trim() === '') {
                return null;
            }

            return {
                text: rawContent,
                sizeErpId: parseInt(section.fontSizeErpId, 10) || globalFontSizeErpId,
            };
        })
        .filter(Boolean);

    if (lines.length === 0 || !fontErpId) {
        return null;
    }

    return {
        lines,
        fontErpId,
        mountingErpId: parseInt(mounting.option?.id_erp, 10) || 0,
        materialErpId: parseInt(config?.selectedMaterialObject?.id_erp, 10) || 0,
        typeErpId,
        colorErpId: colorErpId || 0,
    };
}

export function usePriceCalculation() {
    const configContext = useContext(ConfigContext);
    const priceDispatch = useContext(PriceDispatch);
    const textSections = useContext(TextSectionsListContext);

    // Keep latest values in refs so the debounced callback always reads fresh data
    const textSectionsRef = useRef(textSections);
    const configContextRef = useRef(configContext);
    useEffect(() => {
        textSectionsRef.current = textSections;
    }, [textSections]);
    useEffect(() => { configContextRef.current = configContext; }, [configContext]);

    const debounceRef = useRef(null);
    const abortControllerRef = useRef(null);

    /**
     * @param {Object} [textOverride] - optional { index, rawContent } to patch a section's
     *   rawContent before sending, solving the stale-closure problem on typing.
     */
    const recalculateAllPrices = useCallback((textOverride) => {
        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        // Abort previous in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        debounceRef.current = setTimeout(async () => {
            const sections = textSectionsRef.current;
            const config = configContextRef.current;

            const controller = new AbortController();
            abortControllerRef.current = controller;

            const payload = buildPricingRequest(config, sections, textOverride);

            if (!payload) {
                priceDispatch({ type: 'resetPrice' });
                priceDispatch({ type: 'setPriceLoading', payload: false });
                return;
            }

            priceDispatch({ type: 'setPriceLoading', payload: true });

            try {
                const pricing = await priceService.getConfigurationPrice(payload, controller.signal);

                // Odpowiedź jest aktualna tylko wtedy, gdy w międzyczasie nie poszło nowsze żądanie
                if (controller.signal.aborted) return;

                priceDispatch({ type: 'setPricing', payload: pricing });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Price calculation error:', err);
                    priceDispatch({ type: 'resetPrice' });
                }
            } finally {
                if (!controller.signal.aborted) {
                    priceDispatch({ type: 'setPriceLoading', payload: false });
                }
            }
        }, 400);
    }, [priceDispatch]);

    return { recalculateAllPrices };
}
